import React, { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  Fab,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import SaveIcon from "@material-ui/icons/Save";

import * as StoreInventoryApi from "../../../api/storeInventoryApi";
import * as Users from "../../../api/users";
import { Product, ProductId } from "../../../models/Product";

import { Store } from "../../../models/Store";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../store";
import { MinimalStoreInventoryItem } from "../../../models/StoreInventory";
import { thunkFetchStores } from "../../../store/stores/actions";
import Fuse, { FuseOptions } from "fuse.js";
import { isOk } from "../../../result";

import List from "../../../components/layout/list";
import DatePicker from "../../../components/inputs/datepicker";

import useStyles from "./styles";
import { Currency } from "../../../components/misc/Currency";
import { StoresState } from "../../../store/stores/types";
import { useI18n } from "../../../hooks/useI18n";

const options: FuseOptions<MinimalStoreInventoryItem> = {
  id: undefined,
  includeMatches: false,
  includeScore: false,
  keys: ["gtin", "product.name", "brandName"]
};

interface Props {
  disabled?: boolean;
  onSelected: (product: Product) => void;
}

interface Item {
  id: ProductId;
  name: string;
  size?: string;
  price: number;
  quantity: number;
}

interface Props {}

interface Params {
  customerId?: string;
}

export const RegisterPurchase = () => {
  const styles = useStyles();
  const { t } = useI18n();

  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const storesState = useSelector<AppState, StoresState>(s => s.stores);
  let { stores } = storesState;

  const [query, setQuery] = useState("");
  const [chargeId, setChargeId] = useState("");
  const [userDiscountId, setUserDiscountId] = useState("");
  const [timeOfPurchase, setTimeOfPurchase] = useState(new Date());
  const [selectedStoreId, setSelectedStoreId] = useState<string>();
  const [items, setItems] = useState<Item[]>([]);

  const [products, setProducts] = useState<MinimalStoreInventoryItem[]>([]);

  const filteredProducts: any[] = query
    ? new Fuse(products, options).search(query)
    : products;

  const setQuantity =
    (id: ProductId) =>
    (quantity: number = 0) => {
      setItems(
        items.map(i =>
          i.id === id
            ? {
                ...i,
                quantity
              }
            : i
        )
      );
    };

  const sum = (a: number, b: number) => a + b;

  const addItem = (value: string) => {
    const inventoryItem = products.find(p => p.productId == value);

    if (!inventoryItem) {
      return;
    }

    const existing = items.find(i => i.id === value);

    if (existing) {
      setQuantity(inventoryItem.productId)(existing.quantity + 1);

      return;
    }

    setItems([
      ...items,
      {
        id: inventoryItem.productId,
        name: inventoryItem.productName,
        price: inventoryItem.price ?? 0,
        quantity: 1
      }
    ]);
  };

  const search = (value: string) => {
    setQuery(value);
  };

  const register = async () => {
    if (!selectedStoreId) {
      return;
    }

    if (!params || !params.customerId) {
      return;
    }

    await Users.registerPurchase({
      customerId: params.customerId,
      storeId: selectedStoreId,
      chargeId: chargeId,
      userDiscountId: userDiscountId,
      timeOfPurchase: timeOfPurchase.toISOString(),
      items: items.map(i => ({
        productId: i.id,
        quantity: i.quantity,
        price: i.price
      }))
    });

    navigate(`/users/${params.customerId}`, { replace: true });
  };

  const valid = () => selectedStoreId && chargeId && items.length > 0;

  const onSelectedStore = (value: string) => {
    setSelectedStoreId(value);
  };

  useEffect(() => {
    dispatch(thunkFetchStores());
  }, []);

  useEffect(() => {
    const getProducts = async () => {
      if (!selectedStoreId) {
        return;
      }
      const result = await StoreInventoryApi.list(selectedStoreId);

      if (isOk(result)) {
        return setProducts(result.value.map(i => i));
      }

      console.error(result.details);
      return setProducts([]);
    };

    getProducts();
  }, [selectedStoreId]);

  const renderStoreSelect = () =>
    stores?.length > 0 ? (
      <FormControl>
        <InputLabel id="store-select-label">
          {t("common.formFields.selectAStore")}
        </InputLabel>
        <Select
          defaultValue=""
          labelId="store-select-label"
          onChange={({ target: { value } }) => onSelectedStore(value as string)}
          className={styles.storeSelect}
        >
          {stores.map(store => (
            <MenuItem key={store.id} value={store.id}>
              {store.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : (
      <div>{t("common.messages.noStoresLoaded")}</div>
    );

  const renderProductSelect = () => (
    <Autocomplete
      options={filteredProducts}
      getOptionLabel={item => item.productName}
      renderOption={option => (
        <React.Fragment>
          <Grid container>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={8} className={styles.productSelectItemName}>
                  {option.productName}
                </Grid>
                <Grid item xs={4} className={styles.productSelectItemPrice}>
                  <Currency value={option.price} />
                </Grid>
              </Grid>
            </Grid>

            {option.size && (
              <Grid item xs={12} className={styles.productSelectItemSize}>
                {option.size}
              </Grid>
            )}
          </Grid>
        </React.Fragment>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={t("common.formFields.selectProduct")}
          variant="outlined"
        />
      )}
      onChange={(event: any, newValue: any | null) => {
        if (newValue) {
          addItem(newValue.productId);
        }
      }}
      disabled={filteredProducts.length === 0}
    />
  );

  const renderSelectedItems = () => (
    <List
      items={items}
      renderItem={item => (
        <div>
          <h3 className={styles.selectedItemName}>{item.name}</h3>

          <div className={styles.selectedItemInput}>
            <div className={styles.selectedItemPriceContainer}>
              <div
                className={styles.selectedItemPrice}
              >{`Price per item: ${item.price.toFixed(2)}`}</div>
              <div className={styles.selectedItemPrice}>{`Total price: ${(
                item.price * item.quantity
              ).toFixed(2)}`}</div>
            </div>
            <TextField
              value={item.quantity}
              type="number"
              onChange={({ target: { value } }) => {
                if (value) {
                  setQuantity(item.id)(parseInt(value));
                }
              }}
              inputProps={{ min: 0, max: 99 }}
              className={styles.selectedItemQuantity}
            />
          </div>
        </div>
      )}
      keyExtractor={item => item.id}
    />
  );

  return (
    <Paper className={styles.container}>
      <h2>{t("common.messages.registerPurchase")}</h2>
      {renderStoreSelect()}

      {renderProductSelect()}

      {renderSelectedItems()}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4} className={styles.inputs}>
          <TextField
            label={t("common.formFields.chargeId")}
            value={chargeId}
            onChange={({ target: { value } }) => setChargeId(value)}
            disabled={!selectedStoreId}
          />
        </Grid>
        <Grid item xs={12} sm={4} className={styles.inputs}>
          <TextField
            label={t("common.formFields.userCouponId")}
            value={userDiscountId}
            onChange={({ target: { value } }) => setUserDiscountId(value)}
            disabled={!selectedStoreId}
          />
        </Grid>
        <Grid item xs={12} sm={4} className={styles.inputs}>
          <DatePicker
            value={timeOfPurchase}
            onChange={value => setTimeOfPurchase(value)}
            disabled={!selectedStoreId}
            label={t("common.formFields.timeOfPurchase")}
          />
        </Grid>
      </Grid>

      <div className={styles.total}>
        Total:{" "}
        <Currency value={items.map(i => i.price * i.quantity).reduce(sum, 0)} />
      </div>

      <Fab
        color="primary"
        aria-label="add"
        component="span"
        disabled={!valid()}
        onClick={register}
        className={styles.saveButton}
      >
        <SaveIcon />
      </Fab>
    </Paper>
  );
};
